import type { KitGroup, WizardLine } from '../types/wizard';
import { bomComponentQty, effectiveWhQty, type KitBomItem } from './kitQuantities';
import { lineFilledQty } from './lineQuantities';
import { canonicalTagId, isLeapTag } from './leapDetect';

export type { KitBomItem } from './kitQuantities';
export { effectiveWhQty } from './kitQuantities';

export type SkuByTag = Map<string, string | null> | Record<string, string | null>;

export function normalizeTagId(tag: string): string {
  return canonicalTagId(tag);
}

export function normalizeMaterialCode(code: string): string {
  return code.trim().toUpperCase();
}

function lookupSku(skuByTag: SkuByTag | undefined, tag: string): string | null | undefined {
  if (!skuByTag) return undefined;
  const key = normalizeTagId(tag);
  if (skuByTag instanceof Map) {
    if (skuByTag.has(key)) return skuByTag.get(key) ?? null;
    if (skuByTag.has(tag)) return skuByTag.get(tag) ?? null;
    return undefined;
  }
  if (Object.prototype.hasOwnProperty.call(skuByTag, key)) return skuByTag[key] ?? null;
  if (Object.prototype.hasOwnProperty.call(skuByTag, tag)) return skuByTag[tag] ?? null;
  return undefined;
}

/**
 * SAP often pads Material (e.g. A0400000001) while RFID tags embed the shorter form (A040000001).
 */
export function materialMatchKeys(materialCode: string): string[] {
  const m = normalizeMaterialCode(materialCode);
  if (!m) return [];
  const keys = new Set<string>([m]);
  const sap = m.match(/^(A\d{2})(0*)(\d+)$/i);
  if (sap) {
    const [, prefix, zeros, suffix] = sap;
    for (let i = 0; i <= zeros.length; i++) {
      keys.add(prefix + zeros.slice(i) + suffix);
    }
  }
  return [...keys].sort((a, b) => b.length - a.length);
}

/** Engine SKU equals document material (including SAP pad variants). */
export function skuMatchesMaterial(sku: string, materialCode: string): boolean {
  const skuKeys = new Set(materialMatchKeys(sku));
  if (skuKeys.size === 0) return false;
  return materialMatchKeys(materialCode).some((k) => skuKeys.has(k));
}

export function tagMatchesMaterial(
  tag: string,
  materialCode: string,
  skuByTag?: SkuByTag
): boolean {
  const t = normalizeTagId(tag);
  if (!t) return false;

  if (isLeapTag(t)) {
    const sku = lookupSku(skuByTag, t);
    if (!sku) return false;
    return skuMatchesMaterial(sku, materialCode);
  }

  return materialMatchKeys(materialCode).some((key) => t.includes(key));
}

/** Longest material code matched for this tag (legacy substring or LEAP→SKU). */
export function matchTagToMaterial(
  tag: string,
  materialCodes: string[],
  skuByTag?: SkuByTag
): string | null {
  const t = normalizeTagId(tag);
  if (!t) return null;
  const unique = [...new Set(materialCodes.map(normalizeMaterialCode).filter(Boolean))].sort(
    (a, b) => b.length - a.length
  );

  if (isLeapTag(t)) {
    const sku = lookupSku(skuByTag, t);
    if (!sku) return null;
    for (const code of unique) {
      if (skuMatchesMaterial(sku, code)) return code;
    }
    return null;
  }

  for (const code of unique) {
    if (tagMatchesMaterial(t, code)) return code;
  }
  return null;
}

/**
 * Legacy handleScannedQty: filter tags by material (substring or LEAP SKU), allocate across kit tabs.
 */
export function divideTagsForMaterial(
  tagSet: Set<string>,
  materialCode: string,
  lineQty: number,
  kitGroups: KitGroup[],
  bom: KitBomItem[] | null,
  lines: WizardLine[],
  skuByTag?: SkuByTag
): string[][] {
  const mat = normalizeMaterialCode(materialCode);
  const scannedTags = Array.from(tagSet)
    .map(normalizeTagId)
    .filter((t) => tagMatchesMaterial(t, materialCode, skuByTag));
  const dividedTags: string[][] = [];

  kitGroups.forEach((kit, kIndex) => {
    const matIdx = kit.lines.findIndex(
      (c) => !c.isKitHeader && normalizeMaterialCode(c.materialCode) === mat
    );
    if (matIdx === -1) {
      dividedTags.push([]);
      return;
    }
    const line = kit.lines[matIdx];
    const itemBomQty = bomComponentQty(bom, kit.kitMaterial, mat);
    const whQty =
      itemBomQty != null ? kit.kitQty * itemBomQty : parseInt(String(lineQty), 10) || line.quantity;

    if (scannedTags.length <= whQty) {
      dividedTags.push(scannedTags.splice(0, scannedTags.length));
    } else {
      dividedTags.push(scannedTags.splice(0, whQty));
    }
  });

  if (kitGroups.length === 0) {
    const qty = lineQty;
    if (scannedTags.length <= qty) {
      dividedTags.push(scannedTags.splice(0, scannedTags.length));
    } else {
      dividedTags.push(scannedTags.splice(0, qty));
    }
  }

  return dividedTags;
}

export function tagsForLineInKit(
  tagSet: Set<string>,
  line: WizardLine,
  kitGroups: KitGroup[],
  activeKitIndex: number,
  bom: KitBomItem[] | null,
  lines: WizardLine[],
  skuByTag?: SkuByTag
): string[] {
  const divided = divideTagsForMaterial(
    new Set(Array.from(tagSet).map(normalizeTagId)),
    line.materialCode,
    line.quantity,
    kitGroups,
    bom,
    lines,
    skuByTag
  );
  return divided[activeKitIndex] ?? [];
}

export function syncLinesFromTagSet(params: {
  tagSet: Set<string>;
  lines: WizardLine[];
  kitGroups: KitGroup[];
  bom: KitBomItem[] | null;
  skuByTag?: SkuByTag;
}): WizardLine[] {
  const { tagSet, lines, kitGroups, bom, skuByTag } = params;
  const normalizedTags = Array.from(tagSet).map(normalizeTagId).filter(Boolean);
  const materialLines = lines.filter((l) => !l.isKitHeader && l.materialCode?.trim());
  const materialCodes = materialLines.map((l) => l.materialCode);

  const allocationByMaterial = new Map<string, string[][]>();

  const uniqueMaterials = new Set(materialCodes.map(normalizeMaterialCode));
  uniqueMaterials.forEach((matNorm) => {
    const sample = materialLines.find(
      (l) => normalizeMaterialCode(l.materialCode) === matNorm
    );
    if (!sample) return;
    allocationByMaterial.set(
      matNorm,
      divideTagsForMaterial(
        new Set(normalizedTags),
        sample.materialCode,
        sample.quantity,
        kitGroups,
        bom,
        lines,
        skuByTag
      )
    );
  });

  return lines.map((line) => {
    if (line.isKitHeader) return line;
    const matNorm = normalizeMaterialCode(line.materialCode);
    const divided = allocationByMaterial.get(matNorm);
    if (!divided) return { ...line, rfidCodes: [], scanQty: 0 };
    let kitIndex = 0;
    if (kitGroups.length > 0) {
      const ki = kitGroups.findIndex((g) => g.lines.some((l) => l.id === line.id));
      kitIndex = ki >= 0 ? ki : 0;
    }
    const rfidCodes = [...(divided[kitIndex] ?? [])];
    return { ...line, rfidCodes, scanQty: rfidCodes.length };
  });
}

/** Keep kit group line refs in sync after syncLinesFromTagSet (flat lines get new objects). */
export function applySyncedLinesToKitGroups(
  lines: WizardLine[],
  kitGroups: KitGroup[]
): KitGroup[] {
  if (kitGroups.length === 0) return kitGroups;
  const byId = new Map(lines.map((l) => [l.id, l]));
  return kitGroups.map((g) => ({
    ...g,
    lines: g.lines.map((l) => byId.get(l.id) ?? l),
  }));
}

export function syncWizardFromTagSet(params: {
  tagSet: Set<string>;
  lines: WizardLine[];
  kitGroups: KitGroup[];
  bom: KitBomItem[] | null;
  skuByTag?: SkuByTag;
}): { lines: WizardLine[]; kitGroups: KitGroup[] } {
  const lines = syncLinesFromTagSet(params);
  const kitGroups = applySyncedLinesToKitGroups(lines, params.kitGroups);
  return { lines, kitGroups };
}

export function allScannableComplete(
  lines: WizardLine[],
  kitGroups: KitGroup[],
  bom: KitBomItem[] | null
): boolean {
  const checkLines =
    kitGroups.length > 0
      ? kitGroups.flatMap((g, ki) =>
          g.lines
            .filter((l) => !l.isKitHeader && l.materialCode.trim())
            .map((l) => ({ line: l, kitIndex: ki }))
        )
      : lines
          .filter((l) => !l.isKitHeader && l.materialCode.trim())
          .map((l) => ({ line: l, kitIndex: 0 }));

  if (checkLines.length === 0) {
    return lines.some((l) => !l.isKitHeader && l.scanQty > 0);
  }

  return checkLines.every(({ line, kitIndex }) => {
    const wh = effectiveWhQty(line, kitGroups, kitIndex, bom);
    return lineFilledQty(line, wh) >= wh;
  });
}

/** LEAP tags in the pool that have no usable SKU match on the document. */
export function countUnmatchedLeapTags(params: {
  tagSet: Set<string>;
  lines: WizardLine[];
  skuByTag?: SkuByTag;
}): number {
  const materialCodes = params.lines
    .filter((l) => !l.isKitHeader && l.materialCode?.trim())
    .map((l) => l.materialCode);
  let n = 0;
  for (const raw of params.tagSet) {
    const t = normalizeTagId(raw);
    if (!isLeapTag(t)) continue;
    if (!matchTagToMaterial(t, materialCodes, params.skuByTag)) n += 1;
  }
  return n;
}
